// apps/web/src/components/users/user-deactivate-dialog.tsx — Fase 01, Plano 59 (USR-03 / NFR-08).
// Diálogo de confirmação destrutiva de desativação de usuário.
import { useState, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';

export interface UserDeactivateDialogProps {
  readonly isOpen: boolean;
  readonly userId: string;
  readonly userName: string;
  readonly onClose: () => void;
  readonly onDeactivated: () => void;
}

export function UserDeactivateDialog({
  isOpen,
  userId,
  userName,
  onClose,
  onDeactivated,
}: UserDeactivateDialogProps): ReactElement | null {
  const [justification, setJustification] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleDeactivate = async () => {
    if (!justification.trim()) {
      setError('A justificativa é obrigatória.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const res = await apiMutate(`/api/v1/users/${userId}/deactivate`, {
        method: 'POST',
        body: { justification },
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Falha ao desativar usuário');
      }

      onDeactivated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao desativar usuário.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
        <h2 className="text-xl font-bold text-red-600 dark:text-red-400">Desativar Usuário</h2>
        
        <p className="mt-3 text-sm text-slate-700 dark:text-slate-300">
          Desativar <strong className="font-semibold">{userName}</strong> remove o acesso imediatamente. Qualquer apontamento feito por este usuário, ou por um dispositivo vinculado a ele, passa a ser rejeitado pelo servidor.
        </p>

        <div className="mt-4">
          <label htmlFor="deactivate-justification" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Justificativa de Desativação
          </label>
          <textarea
            id="deactivate-justification"
            value={justification}
            onChange={(e) => { setJustification(e.target.value); }}
            placeholder="Informe a justificativa..."
            className="mt-1 w-full rounded border border-slate-300 p-2 text-sm focus:border-red-500 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        {error && <div className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">{error}</div>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleDeactivate()}
            disabled={isSubmitting}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Desativando...' : 'Desativar usuário'}
          </button>
        </div>
      </div>
    </div>
  );
}
